import { useMemo } from 'react';
import { Card, Spinner } from 'react-bootstrap';
import { useAppContext } from '../../providers/AppProvider.js';
import { peerDeltaFor } from './operatorStripMath.js';

// Who is on the floor, and how each of them is doing — pick one to focus, pick
// several to compare.
//
// The page used to open on aggregate charts with the people buried inside them,
// and the only way to single anyone out was a dropdown in a filter bar. But the
// question this page exists to answer is about a person, so the people come
// first and everything below reacts to who is selected.
//
// Speed is shown against the MEDIAN OF THE OTHERS, not against a target. A
// target is a guess; the median is what this factory actually does on this work,
// which is the only fair comparison — and the same reason the matrices carry a
// peer median per row.

const fmtKm = (m) => {
  if (!m) return '0 km';
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
};

const initialsOf = (name) =>
  String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0])
    .join('')
    .toUpperCase();

// A stable hue per name, so the same person keeps the same colour across every
// panel on the page. Mirrors the badge colouring in the execution history.
const hueOf = (name) => {
  let h = 0;
  for (let i = 0; i < String(name).length; i++) {
    h = (h * 31 + String(name).charCodeAt(i)) % 360;
  }
  return h;
};

/**
 * One operator, as a card you can select.
 *
 * `delta` is null when there is nobody to compare against — with one operator
 * in scope the arithmetic gives 0%, which reads as "exactly average" when the
 * truth is "nobody else ran this". The matrices make the same distinction.
 */
const OperatorCard = ({ row, selected, dimmed, delta, onToggle }) => {
  const { config: { theme } } = useAppContext();
  const hue = hueOf(row._id);
  // A fixed pair of lightnesses only works on one theme: a 92%-light disc is a
  // glaring white dot on a #0f111a page, and a 32% ink is unreadable on it.
  // Swap the two rather than dimming, so the initials keep their contrast.
  const disc = theme === 'dark' ? `hsl(${hue} 45% 22%)` : `hsl(${hue} 70% 92%)`;
  const ink = theme === 'dark' ? `hsl(${hue} 70% 78%)` : `hsl(${hue} 70% 32%)`;
  const speed = row.avgSpeed;

  const trend =
    delta == null ? null : delta > 2 ? 'up' : delta < -2 ? 'down' : 'level';

  const trendUI = {
    up: { icon: '▲', cls: 'text-success', text: `+${Math.round(delta)}% vs peers` },
    down: { icon: '▼', cls: 'text-danger', text: `${Math.round(delta)}% vs peers` },
    level: { icon: '=', cls: 'text-700', text: 'at the median' }
  }[trend];

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={selected}
      className={`operator-chip btn text-start p-0 border-0 bg-transparent ${dimmed ? 'opacity-50' : ''}`}
      style={{ flex: '0 0 auto', width: 172 }}
    >
      <Card
        className={`h-100 ${selected ? 'border-primary' : ''}`}
        style={{ borderWidth: selected ? 2 : 1, transition: 'border-color .12s' }}
      >
        <Card.Body className="p-3">
          <div className="d-flex align-items-center gap-2 mb-2">
            <span
              className="d-inline-flex align-items-center justify-content-center rounded-circle fw-bold"
              style={{
                width: 30,
                height: 30,
                fontSize: 11,
                background: disc,
                color: ink
              }}
            >
              {initialsOf(row._id)}
            </span>
            <span className="fw-semibold text-truncate fs-9" title={row._id}>
              {row._id}
            </span>
          </div>

          <div className="fs-7 fw-bold mb-0">{fmtKm(row.totalProducedLength)}</div>
          <div className="fs-10 text-700 mb-2">
            {row.totalTokens} drum{row.totalTokens === 1 ? '' : 's'}
          </div>

          <div className="fs-9">
            {speed == null ? (
              <span className="text-700">no speed recorded</span>
            ) : (
              <>
                <span className="fw-semibold">{speed.toFixed(0)}</span>
                <span className="text-700"> m/min</span>
              </>
            )}
          </div>

          {trendUI ? (
            <div className={`fs-10 ${trendUI.cls}`}>
              {trendUI.icon} {trendUI.text}
            </div>
          ) : (
            <div className="fs-10 text-700 opacity-75">no peers</div>
          )}
        </Card.Body>
      </Card>
    </button>
  );
};

/**
 * @param rows      operator-summary rows, already filtered by the scope bar
 * @param selected  names currently picked; empty means "everyone"
 */
const OperatorStrip = ({ rows = [], selected = [], onChange, isLoading }) => {
  // Peer medians over EVERYONE in scope, not over the selection — otherwise
  // picking two people would redefine what they are being compared against, and
  // the deltas would move every time you changed the selection.
  const deltaOf = useMemo(() => peerDeltaFor(rows), [rows]);

  const toggle = (name) =>
    onChange(selected.includes(name) ? selected.filter(n => n !== name) : [...selected, name]);

  if (isLoading) {
    return (
      <div className="d-flex align-items-center gap-2 py-4 text-700">
        <Spinner animation="border" variant="primary" size="sm" />
        <span className="fs-9">Loading operators…</span>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <Card className="mb-3">
        <Card.Body className="py-4 text-center text-700 fs-9">
          Nobody produced anything in this period.
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="mb-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span className="fs-9 text-700">
          {selected.length === 0
            ? 'Everyone — pick one to focus, or several to compare'
            : selected.length === 1
              ? `Showing ${selected[0]} only`
              : `Comparing ${selected.length} operators`}
        </span>
        {selected.length > 0 && (
          <button
            className="btn btn-sm btn-link text-decoration-none p-0 fs-9"
            onClick={() => onChange([])}
          >
            Show everyone
          </button>
        )}
      </div>

      {/* A horizontal rail rather than a wrapping grid: the order is meaningful
          (most produced first) and a grid reflows it into rows that read as
          groups when they are not. */}
      <div className="d-flex gap-2 pb-2" style={{ overflowX: 'auto' }}>
        {rows.map(row => (
          <OperatorCard
            key={row._id}
            row={row}
            selected={selected.includes(row._id)}
            dimmed={selected.length > 0 && !selected.includes(row._id)}
            delta={deltaOf(row)}
            onToggle={() => toggle(row._id)}
          />
        ))}
      </div>
    </div>
  );
};

export default OperatorStrip;
